import { useSignal } from '@preact/signals-react/runtime';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';
import type { User } from '~/auth/types';
import { useAuth } from '~/auth/use-auth';
import { useOrganization } from '~/context/organization/use-org';
import { fetchJson } from '~/utils/fetch-json-v2';
import { UUID } from '~/utils/uuid';
import type { Message, MessageStatus } from './types';
import { ensureSubmitControl } from './uitils';

type UseChatApiProps = {
  chatId: string;
  agentUrn?: string;
  onMessageSent?: () => void;
  onResponseReceived?: (message: Message) => void;
};

export function useChatApi({
  chatId,
  agentUrn,
  onMessageSent,
  onResponseReceived,
}: UseChatApiProps) {
  const { user, updateMetadata } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();

  const isLoading = useSignal(false);
  const status = useSignal<MessageStatus>('');

  const assistantContentRef = useRef<string>('');
  const assistantMessageIdRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const sendMessage = useCallback(
    async (message: Message) => {
      try {
        isLoading.value = true;
        status.value = 'submitted';

        assistantMessageIdRef.current = null;
        assistantContentRef.current = '';

        onMessageSent?.();

        const queryParam = agentUrn ? `?agent_urn=${agentUrn}` : '';
        const { data: response, status: statusRes } = await fetchJson<{
          result: { message: Message };
        }>(`/chats/${chatId}${queryParam}`, {
          method: 'POST',
          body: {
            message,
            metadata: {
              org_urn: currentOrganization?.urn,
            },
          },
        });

        const currentOnboardingStatus = user?.metadata?.onboarding;
        if (!currentOnboardingStatus && statusRes === 204) {
          setTimeout(async () => {
            await updateMetadata({
              onboarding: true,
            });
            await navigate({ to: '/' });
          }, 5000);
          return;
        }

        if (!response) {
          status.value = '';
          return;
        }

        const updatedMessage = ensureSubmitControl(response.result.message);

        setTimeout(() => {
          status.value = '';
          onResponseReceived?.({
            ...updatedMessage,
            id: UUID(),
          });
        }, 1000);
      } catch (error) {
        console.error(error);
        status.value = '';
      } finally {
        isLoading.value = false;
      }
    },
    [
      chatId,
      agentUrn,
      currentOrganization?.urn,
      updateMetadata,
      navigate,
      onMessageSent,
      onResponseReceived,
    ],
  );

  const sendOnboardingMessage = useCallback(
    async (userProfile: User['profile']) => {
      if (!user) return;

      const onboardingMessage = `Hola deseo hacer el onboarding, esta es la información de mi cuenta: ${JSON.stringify(
        {
          ...userProfile,
          account_type: user.metadata.usage,
        },
      )}`;

      const userMessage: Message = {
        id: UUID(),
        type: 'human',
        content: onboardingMessage,
        status: 'finished',
      };

      return sendMessage(userMessage);
    },
    [user, sendMessage],
  );

  return {
    isLoading,
    status,
    sendMessage,
    sendOnboardingMessage,
  };
}
